/**
 * PageContainer Component - Phase 2C-1
 * 
 * Standard page content wrapper with consistent padding, max-width, and structure.
 */

import React from 'react';

export interface PageContainerProps {
  children: React.ReactNode;
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const maxWidthStyles = {
  sm: 'max-w-2xl',
  md: 'max-w-4xl',
  lg: 'max-w-6xl',
  xl: 'max-w-7xl',
  '2xl': 'max-w-screen-2xl',
  full: 'max-w-full',
};

const paddingStyles = {
  none: '',
  sm: 'px-4 py-4',
  md: 'px-6 py-6',
  lg: 'px-8 py-8',
};

export function PageContainer({
  children,
  className = '',
  maxWidth = 'xl',
  padding = 'md',
}: PageContainerProps): JSX.Element {
  return (
    <div
      className={`
        mx-auto w-full
        ${maxWidthStyles[maxWidth]}
        ${paddingStyles[padding]}
        ${className}
      `.trim()}
    >
      {children}
    </div>
  );
}

/**
 * PageHeader Component
 * 
 * Consistent page title and actions bar.
 */
export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  backLink?: {
    label: string;
    to: string;
  };
  breadcrumbs?: Array<{
    label: string;
    to?: string;
  }>;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  actions,
  backLink,
  breadcrumbs,
  className = '',
}: PageHeaderProps): JSX.Element {
  return (
    <div className={`mb-6 ${className}`}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.label}>
              {index > 0 && <ChevronRightIcon className="w-4 h-4" />}
              {crumb.to ? (
                <a
                  href={crumb.to}
                  className="hover:text-slate-200 transition-colors"
                >
                  {crumb.label}
                </a>
              ) : (
                <span className="text-slate-300">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}
      
      {/* Back Link */}
      {backLink && (
        <a
          href={backLink.to}
          className="inline-flex items-center gap-1 text-sm text-slate-400 
                     hover:text-slate-200 transition-colors mb-3"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {backLink.label}
        </a>
      )}
      
      {/* Title Row */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">{title}</h1>
          {subtitle && (
            <p className="mt-1 text-slate-400">{subtitle}</p>
          )}
        </div>
        
        {actions && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * PageSection Component
 * 
 * Section within a page with optional title.
 */
export interface PageSectionProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function PageSection({
  title,
  subtitle,
  actions,
  children,
  className = '',
}: PageSectionProps): JSX.Element {
  return (
    <section className={`mb-8 ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between mb-4">
          <div>
            {title && (
              <h2 className="text-lg font-semibold text-slate-200">{title}</h2>
            )}
            {subtitle && (
              <p className="text-sm text-slate-400">{subtitle}</p>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-2">{actions}</div>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * PageCard Component
 * 
 * Card container for page content sections.
 */
export interface PageCardProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  noPadding?: boolean;
}

export function PageCard({
  title,
  subtitle,
  actions,
  children,
  className = '',
  noPadding = false,
}: PageCardProps): JSX.Element {
  return (
    <div
      className={`
        bg-slate-800 border border-slate-700 rounded-lg
        ${className}
      `.trim()}
    >
      {(title || actions) && (
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div>
            {title && (
              <h3 className="font-semibold text-slate-200">{title}</h3>
            )}
            {subtitle && (
              <p className="text-sm text-slate-400">{subtitle}</p>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-2">{actions}</div>
          )}
        </div>
      )}
      <div className={noPadding ? '' : 'p-6'}>
        {children}
      </div>
    </div>
  );
}

// Icon Components
function ChevronRightIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ArrowLeftIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}

export default PageContainer;
